<script setup lang="ts">
import { useForm } from 'vee-validate';
import { computed, ref, watch, watchEffect } from 'vue';

import { defaultAddress, defaultIncomingProperty } from '@/helpers/builder';
import {
  incomingPropertyTypeOptions,
  commercialIncomingPropertyTypeOptions,
  propertyPurposeOptions,
  PropertyPurpose,
  PropertyType,
  StampDutyStatus,
  stampDutyStatusOptions,
  commercialTypeOptions,
} from '@/helpers/const';
import { useCalcApi } from '@/helpers/useCalcApi';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';

import IncomingPropertyAddress from '@/components/application/incoming-property-address.vue';
import IncomingPropertyReceiveRentalIncome from '@/components/application/incoming-property-receive-rental-income.vue';
import IncomingPropertyExpectedRentalIncome from '@/components/application/incoming-property-expected-rental-income.vue';
import IncomingPropertyTransferFeeCovered from '@/components/application/incoming-property-transfer-fee-covered.vue';
import IncomingPropertyIsNewBuild from '@/components/application/incoming-property-is-new-build.vue';
import IncomingPropertyContractExchangedBeforeMay2026 from '@/components/application/incoming-property-contract-exchanged-before-may-2026.vue';
import { brokerQuoteIncomingPropertySchema } from '@/validation';
import { brokerQuoteIncomingPropertySimpleSchema } from '@/validation/schemas/simple/incoming-property';

import ScenarioTypeTabs from './modules/ScenarioTypeTabs.vue';
import SimpleIncomingPropertyAddress from './modules/IncomingProperties/SimpleIncomingPropertyAddress.vue';
import PropertyDetailsStep from './PropertyDetailsStep.vue';

import type { DepositPaidType } from '@/api/enums';
import type { Address, IncomingProperty, IncomingPropertyEntry } from '@/types';

type IncomingPropertyWithSettlementDate = IncomingPropertyEntry & { settlementDate: string };

const depositTypeOptions: Record<DepositPaidType, string> = {
  ['cash_payment']: 'Cash payment',
  ['deposit_bond']: 'Deposit bond',
};

const initAddress: Address = {
  addressLine: '',
  locality: '',
  state: '',
  postcode: '',
  countryCode: 'AU',
};

let propertyCounter = 0;

const { state, loanApp, sectionStates, saveAndGoNext, goBack, send } = useUserQuoteProcess();
const { isCommercialQuoteFlowEnabled, isNegativeGearingFy27Enabled } = useFeatureFlags();
const calc = useCalcApi();

const toggle = ref(true);
const isLoading = ref(false);

const incomingPropertiesType = computed(() => sectionStates.value.incomingProperties);

const validationSchema = computed(() => {
  if (incomingPropertiesType.value === 'simple') {
    return brokerQuoteIncomingPropertySimpleSchema;
  } else {
    return brokerQuoteIncomingPropertySchema;
  }
});

const singleSecurityRequired = computed<boolean>(() => {
  return (
    values.propertyType === PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME ||
    values.propertyType === PropertyType.VACANT_LAND
  );
});

// note: "already_paid" is only valid if contract for sale exists
const validStampDutyPaymentOptions = computed(() =>
  Object.fromEntries(
    Object.entries(stampDutyStatusOptions).filter(
      ([id]) => values.contractForSale || id !== StampDutyStatus.ALREADY_PAID,
    ),
  ),
);

const {
  values,
  setFieldValue,
  meta,
  submitCount,
  handleSubmit: validateSubmit,
  resetForm,
} = useForm<IncomingPropertyWithSettlementDate>({
  validationSchema,
  initialValues: loanApp.value.incomingProperties.length
    ? { ...loanApp.value.incomingProperties[0] }
    : {
        id: `incoming-property-${propertyCounter++}`,
        address: incomingPropertiesType.value === 'simple' ? initAddress : defaultAddress(),
      },
  validateOnMount: false,
});

const showNegativeGearingQuestions = computed(
  () =>
    isNegativeGearingFy27Enabled() &&
    !singleSecurityRequired.value &&
    values.propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY,
);

const showContractExchangeQuestion = computed(
  () => showNegativeGearingQuestions.value && values.isNewBuild === false && values.contractForSale === true,
);

watch(incomingPropertiesType, (newType) => {
  if (newType === 'simple') {
    setFieldValue('address', initAddress);
  } else {
    setFieldValue('address', defaultAddress());
  }
});

watchEffect(() => {
  if (!values.contractForSale && values.stampDutyStatus === StampDutyStatus.ALREADY_PAID) {
    setFieldValue('stampDutyStatus', undefined);
  }
  if (singleSecurityRequired.value) {
    setFieldValue('address', undefined);
  }
});

if (loanApp.value.incomingProperties.length) {
  resetForm({ values: loanApp.value.incomingProperties[0] });
}

const handleSubmit = validateSubmit(async (values) => {
  try {
    isLoading.value = true;

    if (singleSecurityRequired.value) {
      send({
        type: 'UPDATE',
        loanApp: {
          ...loanApp.value,
          loanType: 'single_security',
        },
      });
      send({ type: 'GOTO', step: 'uqLoanPurpose' });
      return;
    }

    const property: IncomingProperty = Object.assign(defaultIncomingProperty(), values);

    property.stampDuty = await calc.getStampDuty(property);

    saveAndGoNext({
      incomingProperties: [property],
      settlementDate: values.settlementDate,
    });
  } catch (error) {
    console.error('Failed to save draft:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <PropertyDetailsStep v-if="isCommercialQuoteFlowEnabled()" />
  <template v-else>
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        Tell us about the property your client is
        <BxHighlight>buying</BxHighlight>?
      </h2>
    </div>
    <div class="section-panel md:p-6">
      <div class="mb-6">
        <ScenarioTypeTabs section-key="incomingProperties" title="Buying" base-test-id="incoming-properties" />
      </div>
      <form @submit="handleSubmit">
        <BxExpander v-model="toggle" separator data-test-id="incoming-property-expander">
          <template #header>
            <div class="px-4 py-5 sm:px-6">
              <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
                <h3 class="flex items-center text-lg font-medium leading-6 text-gray-900">
                  <span class="mr-3 hidden md:block"><BxIcon name="incomingProperty" /></span>
                  <span>Property details</span>
                </h3>
              </div>
            </div>
          </template>
          <template #body>
            <div class="p-6">
              <BxField v-slot="{ field }" name="propertyType">
                <BxSelect
                  v-bind="field"
                  :id="`${values.id}-incoming-property-type`"
                  :list-items="
                    isCommercialQuoteFlowEnabled() ? commercialIncomingPropertyTypeOptions : incomingPropertyTypeOptions
                  "
                  :data-test-id="`${values.id}-incoming-property-type`"
                  label="Property Type"
                  label-type="floating"
                  placeholder="Select property type..."
                  class="form-control"
                />
                <div v-if="singleSecurityRequired && values.propertyType" class="form-control mt-6">
                  <p>
                    This property type requires a single security bridging loan, please select the button below to
                    complete the single security application.
                  </p>
                </div>
              </BxField>

              <div v-if="values.propertyType === PropertyType.COMMERCIAL" class="form-control">
                <BxField v-slot="{ field }" name="commercialType">
                  <BxSelect
                    v-bind="field"
                    :id="`${values.id}-commercial-type`"
                    :list-items="commercialTypeOptions"
                    :data-test-id="`${values.id}-commercial-type`"
                    label="Commercial Type"
                    label-type="floating"
                    placeholder="Select commercial type..."
                  />
                </BxField>
              </div>
              <template v-if="!singleSecurityRequired && values.propertyType">
                <SimpleIncomingPropertyAddress v-if="incomingPropertiesType === 'simple'" :id="values.id" />
                <IncomingPropertyAddress v-if="incomingPropertiesType === 'comprehensive'" :id="values.id" />
                <BxField v-slot="{ field }" name="propertyPurpose">
                  <BxRadio
                    v-bind="field"
                    :id="`${values.id}-property-purpose`"
                    :options="propertyPurposeOptions"
                    :data-test-id="`${values.id}-property-purpose`"
                    label="What is the purpose of this property?"
                    class="form-control"
                    block
                  />
                </BxField>
                <IncomingPropertyReceiveRentalIncome :id="values.id" />
                <IncomingPropertyExpectedRentalIncome v-if="values.receiveRentalIncome" :id="values.id" />
                <BxField v-slot="{ field }" name="contractForSale">
                  <BxRadio
                    v-bind="field"
                    :id="`${values.id}-contract-of-sale`"
                    :data-test-id="`${values.id}-contract-of-sale`"
                    label="Do you have a contract for sale?"
                    class="form-control"
                    boolean
                  />
                </BxField>
                <IncomingPropertyIsNewBuild v-if="showNegativeGearingQuestions" :id="values.id" />
                <IncomingPropertyContractExchangedBeforeMay2026 v-if="showContractExchangeQuestion" :id="values.id" />
                <template v-if="values.contractForSale !== undefined">
                  <BxField v-slot="{ field }" name="settlementDate">
                    <BxDatePicker
                      v-bind="field"
                      :id="`${values.id}-${values.contractForSale ? 'settlement-date' : 'estimated-purchase-date'}`"
                      :label="values.contractForSale ? 'Settlement Date' : 'Estimated Purchase Date'"
                      :min-date="new Date()"
                      :data-test-id="`${values.id}-settlement-date-estimated-purchase-date`"
                      label-type="floating"
                      placeholder="DD/MM/YYYY"
                      class="form-control"
                    />
                  </BxField>
                  <BxField v-slot="{ field }" name="purchaseAmount">
                    <BxNumberInput
                      v-bind="field"
                      :id="`${values.id}-${values.contractForSale ? 'purchase-price' : 'estimated-purchase-price'}`"
                      :label="values.contractForSale ? 'Purchase Price' : 'Estimated Purchase Price'"
                      :data-test-id="`${values.id}-purchase-price-estimated-purchase-price`"
                      label-type="floating"
                      leading-symbol="$"
                      class="form-control"
                    />
                  </BxField>
                  <BxField v-slot="{ field }" name="depositAmount">
                    <BxNumberInput
                      v-bind="field"
                      :id="`${values.id}-${values.contractForSale ? 'deposit-amount' : 'estimated-deposit-amount'}`"
                      :label="values.contractForSale ? 'Deposit Amount' : 'Estimated Deposit Amount'"
                      :data-test-id="`${values.id}-deposit-amount-estimated-deposit-amount`"
                      label-type="floating"
                      leading-symbol="$"
                      class="form-control"
                    />
                  </BxField>
                  <BxField v-slot="{ field }" name="depositType">
                    <BxRadio
                      v-bind="field"
                      :id="`${values.id}-deposit-type`"
                      :options="depositTypeOptions"
                      :data-test-id="`${values.id}-deposit-type`"
                      :label="values.contractForSale ? 'How was the deposit paid?' : 'How will the deposit be paid?'"
                      label-type="default"
                      class="form-control"
                    />
                  </BxField>
                  <BxField v-slot="{ field }" name="stampDutyStatus">
                    <BxRadio
                      v-bind="field"
                      :id="`${values.id}-stamp-duty-paid`"
                      :options="validStampDutyPaymentOptions"
                      :data-test-id="`${values.id}-stamp-duty-paid`"
                      label="How will stamp duty be paid?"
                      class="form-control"
                      block
                    />
                  </BxField>
                  <IncomingPropertyTransferFeeCovered :id="values.id" />
                </template>
              </template>
            </div>
            <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
              Please review the error messages above.
            </BxErrorPanel>
            <div class="p-6">
              <BxButton
                id="incoming-properties-continue"
                :loading="isLoading"
                data-test-id="incoming-properties-continue"
                variant="primary"
                type="submit"
              >
                {{ singleSecurityRequired ? 'Single security' : 'Save and continue' }}
              </BxButton>
              <BxButton
                v-if="state.nextEvents.includes('BACK')"
                data-test-id="incoming-properties-back"
                variant="link"
                class="ml-4"
                @click="goBack()"
              >
                Back
              </BxButton>
            </div>
          </template>
        </BxExpander>
      </form>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.link {
  @apply cursor-pointer text-bridgit-navy underline hover:text-bridgit-vibrantGreen;
}
</style>
